import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, type NavLinkProps } from 'react-router-dom';
import { API_BASE } from '../client/config';
import type { DeviceGroup, GroupActor } from '../store/deviceGroups';
import { PageContainer } from '../nav/PageContainer';
import { PageHeader } from '../nav/Breadcrumb';
import { Button, SearchInput } from './ui';
import { ContentSkeleton } from './skeleton/ContentSkeleton';
import { useAuthMe } from '../nav/authMe';

// =============================================================================
// 관리 이력(그룹 운영 계정) — 2026-09-16 검수 S2·S4·S17·P3 반영.
//
// 이 화면의 독자는 개발자가 아니라 기관 담당자다. 그래서 한 행은 "누가 · 무엇을 ·
// 어디에"라는 한 문장으로 읽힌다. 디버깅용 원본 복사는 두지 않는다(SCDS-68).
// 기본 이력 유형은 전체다(SCDS-78). 시스템 폴링이 만든 읽기 거부는 서버가 아예
// 감사에 남기지 않는다(services/groupScope.ts auditableDenial).
// =============================================================================

type HistoryScope = { actor: GroupActor; groups: DeviceGroup[] };
type HistoryAccess = { kind: 'loading' | 'denied' } | { kind: 'error'; message: string }
  | { kind: 'legacy' } | { kind: 'group'; scope: HistoryScope };
export type GroupActivityRow = {
  id: number; org_id: string; group_id: string | null; actor_user_id: string | null;
  /** 서버가 users에서 채워 주는 수행자 표시명·이메일(검수 S17). 옛 응답에는 없을 수 있다. */
  actor_name?: string | null; actor_email?: string | null;
  actor_login_id?: string | null;
  /** 서버가 한 모양으로 맞춰 준 대상 기기(SCDS-88). name=null 은 지금은 지워진 기기. 옛 응답에는 없다. */
  target_devices?: Array<{ id: string; name: string | null }>;
  /** 승인 이력이 가리키는 요청의 종류(content·schedule…) — 콘텐츠 승인은 대상 기기가 없다. */
  request_kind?: string | null;
  action: string; before_json: string | null; after_json: string | null; created_at: string;
};
const historyRole = (role: string | null) => role === 'Owner' || role === 'Admin';
const canReadGroup = (actor: GroupActor, groupId: string | null) => historyRole(actor.role) &&
  (actor.central || (groupId !== null && actor.groups.some(g => g.groupId === groupId && historyRole(g.role))));

async function readJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}/api/${path}`, { credentials: 'include', cache: 'no-store', ...(signal ? { signal } : {}) });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(response.status === 403 ? '관리 이력 조회 권한이 없거나 변경되었습니다.' : body?.error || `조회 실패 (HTTP ${response.status})`);
  }
  return response.json() as Promise<T>;
}

// 2026-09-16 검수 S2(b) — 상단 네비(HistoryNavigation)와 본문이 같은 훅을 쓴다.
// 각자 부르면 /api/device-groups 가 같은 틱에 두 번 연속(0~4ms) 나갔다. 값을
// 캐시하지는 않는다(권한 변경은 즉시 다시 읽어야 한다) — 이미 날아간 요청에만
// 올라탄다. 요청을 공유하므로 abort 신호는 붙이지 않고, 구독자가 각자 자기
// controller로 폐기 여부를 판단한다.
let inflightScope: Promise<HistoryScope> | null = null;
export function readScope(): Promise<HistoryScope> {
  if (inflightScope) return inflightScope;
  const pending: Promise<HistoryScope> = readJson<HistoryScope>('device-groups')
    .finally(() => { if (inflightScope === pending) inflightScope = null; });
  inflightScope = pending;
  return pending;
}

/** One permission decision for the history page and its navigation entry. */
export function useHistoryAccess() {
  // /api/auth/me 는 공용 캐시 한 곳에서만 읽는다(nav/authMe) — 화면마다 직접 부르면
  // 같은 질문을 계정 수만큼 반복하게 된다.
  const { loading: authLoading, me } = useAuthMe();
  const [access, setAccess] = useState<HistoryAccess>({ kind: 'loading' });
  const [revision, setRevision] = useState(0);
  const active = useRef<AbortController | null>(null);
  const reload = useCallback(() => {
    active.current?.abort();
    setAccess({ kind: 'loading' });
    setRevision(value => value + 1);
  }, []);
  useEffect(() => {
    if (authLoading) { setAccess({ kind: 'loading' }); return; }
    const controller = new AbortController(); active.current = controller;
    setAccess({ kind: 'loading' });
    void (async () => {
      // BO keeps the existing global endpoint, including operators without a
      // membership in the selected FO organization.
      if (me?.adminRole === 'h9_super') { setAccess({ kind: 'legacy' }); return; }
      const scope = await readScope();
      if (controller.signal.aborted) return;
      const allowed = historyRole(scope.actor.role) && (!scope.actor.enforced || scope.actor.central ||
        scope.actor.groups.some(g => canReadGroup(scope.actor, g.groupId)));
      setAccess(!allowed ? { kind: 'denied' } : scope.actor.enforced ? { kind: 'group', scope } : { kind: 'legacy' });
    })().catch((error: unknown) => {
      if (!controller.signal.aborted) setAccess({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
    });
    return () => controller.abort();
  }, [revision, authLoading, me]);
  useEffect(() => {
    window.addEventListener('bricklit:scope-changed', reload);
    // 승인 이력은 편성·승인이 바뀔 때도 다시 읽어야 한다(감추지 않고 값만 갱신).
    window.addEventListener('bricklit:workflow-changed', reload);
    return () => {
      window.removeEventListener('bricklit:scope-changed', reload);
      window.removeEventListener('bricklit:workflow-changed', reload);
    };
  }, [reload]);
  return { access, reload, revision };
}

export function HistoryNavigation(props: Pick<NavLinkProps, 'className' | 'children' | 'title'>): JSX.Element | null {
  const { access } = useHistoryAccess();
  return access.kind === 'legacy' || access.kind === 'group' ? <NavLink to="/history" {...props} /> : null;
}

const ACTIONS: Record<string, string> = {
  'approval.submit': '승인 요청', 'approval.approve': '승인', 'approval.reject': '반려', 'approval.cancel': '요청 취소',
  'group.create': '그룹 생성', 'group.update': '그룹 변경', 'group.archive': '그룹 보관',
  'group.device_move': '기기 그룹 이동', 'group.member_assign': '담당 권한 변경', 'group.scope_activate': '그룹 운영 활성화',
  'content.scope_change': '콘텐츠 공유 변경', 'scope.denied': '접근 거부',
  // 서버가 같은 표에 남기는 나머지 이력 — 원문 action 이름이 그대로 보이던 것(SCDS-88 영상).
  'group.scope_autoclassify': '콘텐츠 자동 분류',
  'device.connection': '기기 연결', 'device.diagnostics.transition': '기기 상태 변화',
  'device.idle_notice': '대기 화면 변경', 'device.pairing_claim': '기기 등록',
  'delivery.activated': '송출 시작', 'delivery.reused': '송출 이어 받기', 'delivery.completed': '송출 완료',
  'delivery.stopped': '송출 중지', 'delivery.superseded': '송출 교체', 'delivery.expired': '송출 만료', 'delivery.failed': '송출 실패',
  'schedule.assignment.create': '스케줄 등록', 'schedule.assignment.update': '스케줄 수정', 'schedule.assignment.delete': '스케줄 삭제',
  'schedule.assignment.approve': '스케줄 승인', 'schedule.assignment.reject': '스케줄 반려',
  'schedule.assignment.cancel': '스케줄 승인 요청 취소', 'schedule.assignment.reorder': '스케줄 순서 변경',
  'schedule.assignment.content_refresh': '스케줄 콘텐츠 갱신', 'schedule.assignment.skip': '스케줄 하루 제외',
  'schedule.assignment.update_request': '스케줄 수정 요청', 'schedule.assignment.delete_request': '스케줄 삭제 요청',
  'content.idle_publish': '대기 화면 게시', 'content.idle_share': '대기 화면 공유', 'content.weather_location': '날씨 위치 변경',
  'org.branding.update': '조직 디자인 변경', 'org.branding.reset': '조직 디자인 초기화',
};
/** 기기와 무관한 이력 — 대상 기기 칸에 '기록 없음' 대신 '해당 없음'을 쓴다(SCDS-88). */
const deviceless = (action: string, kind: string) => kind === 'content' ||
  /^(group\.(?!device_move)|content\.|org\.|scope\.)/.test(action);
// 이력 유형 필터 — 값 하나가 여러 action domain을 묶을 수 있다. 기본값은 '전체'
// (SCDS-78). 사람이 한 일만 보려면 '운영 이력'을 고른다. 시스템 폴링이 만든 읽기
// 거부는 서버가 감사에 남기지 않으므로 전체에 섞여도 목록이 묻히지 않는다.
const CATEGORY_DOMAINS: Record<string, readonly string[]> = {
  ops: ['approval', 'group', 'content'],
  approval: ['approval'], group: ['group'], content: ['content'], scope: ['scope'],
};
const DEFAULT_CATEGORY = '';

const MODES: Record<string, string> = { auto: '자동 승인', central: '중앙 승인', group: '그룹 승인' };
const KINDS: Record<string, string> = { content: '콘텐츠', schedule: '송출', timeline: '타임라인', 'schedule-type': '스케줄 타입' };
function record(raw: string | null): Record<string, unknown> {
  try { const value: unknown = JSON.parse(raw ?? 'null'); return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}; }
  catch { return {}; }
}
const text = (value: unknown) => typeof value === 'string' || typeof value === 'number' ? String(value) : '';
type Target = { id: string; name: string | null };
function targets(row: GroupActivityRow, after: Record<string, unknown>): Target[] {
  // 서버가 채워 준 값이 우선이다 — deviceId 하나만 남는 이력·요청 취소/반려까지 담겨 있다(SCDS-88).
  if (Array.isArray(row.target_devices)) return row.target_devices;
  const value = after.targetDevices ?? after.targetDevicesAtSubmission;
  if (!Array.isArray(value)) return typeof after.deviceId === 'string' ? [{ id: after.deviceId, name: null }] : [];
  return value.flatMap((item: unknown) => {
    if (!item || typeof item !== 'object' || !('id' in item) || typeof item.id !== 'string') return [];
    return [{ id: item.id, name: 'name' in item && typeof item.name === 'string' ? item.name : item.id }];
  });
}
function dateTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '일시 없음' : date.toLocaleString('ko-KR', { timeZone: 'Asia/Seoul', hourCycle: 'h23' });
}

export function GroupActivityHistory({ scope, reload }: { scope: HistoryScope; reload: () => void }): JSX.Element {
  const { me } = useAuthMe();
  const [result, setResult] = useState<{ scope: HistoryScope; rows: GroupActivityRow[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [group, setGroup] = useState('');
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [from, setFrom] = useState(''); const [to, setTo] = useState('');
  const [search, setSearch] = useState(''); const [query, setQuery] = useState('');
  // 기본 20건 — 10건이면 한 화면에서 흐름이 끊기고, 50건이면 아래 페이지네이션이
  // 스크롤 밖으로 밀린다(P3).
  const [page, setPage] = useState(1); const [pageSize, setPageSize] = useState(20);
  const permitted = historyRole(scope.actor.role) && (scope.actor.central || scope.actor.groups.some(g => canReadGroup(scope.actor, g.groupId)));
  useEffect(() => {
    const controller = new AbortController();
    setResult(null); setError(null); setLoading(true); setPage(1); setGroup('');
    if (!permitted) { setLoading(false); return () => controller.abort(); }
    void readJson<GroupActivityRow[]>('group-workflow/activity', controller.signal).then(rows => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(rows) || rows.some(row => row.org_id !== scope.actor.orgId)) throw new Error('조회 조직이 변경되었습니다. 권한을 다시 확인해 주세요.');
      setResult({ scope, rows: rows.filter(row => canReadGroup(scope.actor, row.group_id)).sort((a, b) => b.id - a.id) });
    }).catch((cause: unknown) => {
      if (!controller.signal.aborted) { setResult(null); setError(cause instanceof Error ? cause.message : String(cause)); }
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [scope, permitted]);

  // 보관·삭제되어 목록에 없는 그룹은 UUID 대신 사람이 읽는 말로(SCDS-68).
  const groupName = (id: string | null) => id === null ? '공통' : scope.groups.find(g => g.id === id)?.name ?? '보관된 그룹';
  const rows = !loading && !error && result?.scope === scope && permitted ? result.rows : [];
  const items = rows.map(row => {
    const before = record(row.before_json); const after = record(row.after_json);
    const mode = text(after.via ?? after.mode);
    const action = row.action === 'approval.approve' && MODES[mode] ? MODES[mode]
      : row.action === 'device.connection' && after.event === 'disconnected' ? '기기 연결 끊김'
      : ACTIONS[row.action] ?? row.action;
    const details: string[] = [];
    if (after.requestId) details.push(`요청 ${text(after.requestId)}`);
    if (after.kind) details.push(KINDS[text(after.kind)] ?? text(after.kind));
    if (row.action === 'approval.submit' && MODES[mode]) details.push(MODES[mode]!);
    if (after.policyVersion) details.push(`승인 정책 버전 ${text(after.policyVersion)}`);
    if (after.name) details.push(before.name && before.name !== after.name ? `${text(before.name)} → ${text(after.name)}` : text(after.name));
    if (after.approvalMode) details.push(`승인 방식: ${MODES[text(before.approvalMode)] ?? text(before.approvalMode)} → ${MODES[text(after.approvalMode)] ?? text(after.approvalMode)}`);
    // 그룹 이동만 '어디서 → 어디로'를 쓴다. 다른 이력의 deviceId는 대상 기기 칸이 보여 준다 —
    // 예전엔 기기 연결·스케줄 이력마다 "기기 <UUID> · 공통 → 공통"이 붙었다(SCDS-88).
    if (row.action === 'group.device_move' && after.deviceId) details.push(`${groupName(typeof before.groupId === 'string' ? before.groupId : null)} → ${groupName(typeof after.groupId === 'string' ? after.groupId : null)}`);
    if (typeof after.startDate === 'string' && typeof after.endDate === 'string') details.push(after.startDate === after.endDate ? after.startDate : `${after.startDate} ~ ${after.endDate}`);
    if (after.userId) details.push(`계정 ${text(after.userId)}`);
    if (typeof after.central === 'boolean') details.push(`중앙 권한 ${after.central ? '부여' : '해제'}`);
    if (Array.isArray(after.assignments)) {
      const grants = (value: unknown): Map<string, string> => new Map(Array.isArray(value) ? value.flatMap(grant =>
        grant && typeof grant === 'object' && typeof grant.groupId === 'string' && typeof grant.role === 'string'
          ? [[grant.groupId, grant.role] as [string, string]] : []) : []);
      const previous = grants(before.assignments); const next = grants(after.assignments);
      for (const id of new Set([...previous.keys(), ...next.keys()])) {
        if (previous.get(id) !== next.get(id)) details.push(`${groupName(id)}: ${previous.get(id) ?? '미배정'} → ${next.get(id) ?? '해제'}`);
      }
    }
    if (after.id) details.push(text(after.id));
    if (typeof after.sharedToAll === 'boolean') details.push(`전체 공유 ${after.sharedToAll ? '허용' : '해제'}`);
    if (after.permission) details.push(`요청 권한: ${text(after.permission)}`);
    if (after.routePattern || after.path) details.push(`${text(after.method)} ${text(after.routePattern ?? after.path)}`.trim());
    // 수행자 표시(S17) — 서버가 이름·이메일을 얹어 준다(/group-workflow/activity).
    // 이름이 없는 옛 기록만 "내가 한 일"을 현재 계정과 대조하고, 그래도 모르면
    // 원본 id를 title로 남긴다. 없는 이름을 지어내지 않는다.
    const isSelf = Boolean(row.actor_user_id && scope.actor.userId && row.actor_user_id === scope.actor.userId);
    const named = row.actor_login_id ?? row.actor_email ?? row.actor_name;
    const actorLabel = row.action === 'approval.approve' && after.via === 'auto' ? '시스템 (자동 승인)'
      : !row.actor_user_id ? '시스템'
      : named ? named
      : isSelf ? `나${me?.email ? ` (${me.email})` : ''}`
      : row.actor_user_id;
    return { ...row, actorLabel, actorId: row.actor_user_id, groupName: groupName(row.group_id), actionLabel: action,
      details: details.join(' · ') || '추가 기록 없음', targets: targets(row, after),
      noTargets: deviceless(row.action, text(after.kind) || row.request_kind || '') ? '해당 없음' : '대상 기록 없음' };
  });
  const dateError = from && to && from > to ? '시작일은 종료일보다 늦을 수 없습니다.' : null;
  const filtered = items.filter(row => {
    const time = Date.parse(row.created_at);
    return !dateError && (!group || (group === 'central' ? row.group_id === null : row.group_id === group)) &&
      (!category || (CATEGORY_DOMAINS[category] ?? [category]).some(domain => row.action.startsWith(domain + '.'))) &&
      (!from || time >= Date.parse(`${from}T00:00:00+09:00`)) && (!to || time <= Date.parse(`${to}T23:59:59.999+09:00`)) &&
      (!query || `${row.actorLabel} ${row.actor_email ?? ''} ${row.actor_user_id ?? '시스템'} ${row.groupName} ${row.actionLabel} ${row.details} ${row.targets.map(t => `${t.name ?? ''} ${t.id}`).join(' ')}`.toLowerCase().includes(query.toLowerCase()));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const selectClass = 'rounded border border-line bg-panel-2 px-3 py-2 text-[14px] text-text';

  return <PageContainer fluid column={false}>
    <PageHeader items={[{ label: '관리 이력' }]} actions={<Button disabled={loading} onClick={reload}>새로고침</Button>} />
    {!permitted ? <p role="alert">관리 이력 조회 권한이 없습니다. 중앙 관리자 또는 담당 그룹 관리자 권한이 필요합니다.</p> : <>
      {/* 모바일은 필터 다섯 칸을 두 열 격자로 — 세 줄이면 다 보이니 따로 접지 않는다. 검색은 한 줄 전체. */}
      <div className="mb-4 flex flex-wrap items-end gap-3 max-md:grid max-md:grid-cols-2 max-md:items-start max-md:gap-2 max-md:[&>*]:min-w-0 max-md:[&_select]:h-[42px] max-md:[&_input]:h-[42px] max-md:[&_input]:w-full max-md:[&_select]:w-full max-md:[&>*:last-child]:col-span-2">
        <label className="flex flex-col gap-1 text-[13px]">그룹<select className={selectClass} value={group} onChange={e => { setGroup(e.target.value); setPage(1); }}>
          <option value="">전체 허용 그룹</option>{scope.actor.central && <option value="central">공통</option>}
          {scope.groups.filter(g => canReadGroup(scope.actor, g.id)).map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select></label>
        {/* 기본값은 '전체'(SCDS-78). 셀렉트의 자리·크기는 그대로 둔다(2026-09-16 대교 확정). */}
        <label className="flex flex-col gap-1 text-[13px]">이력 유형<select className={selectClass} value={category} onChange={e => { setCategory(e.target.value); setPage(1); }}>
          <option value="">전체</option>
          <option value="ops">운영 이력 (승인·그룹·콘텐츠)</option>
          <option value="approval">승인</option><option value="group">그룹 운영</option><option value="content">콘텐츠 공유</option><option value="scope">접근 거부</option>
        </select></label>
        {/* 시작일·종료일은 옆 셀렉트와 같은 꼴(라벨 13px · 입력 14px · 높이 36px) — 공용 Field 기본(17px 모노)이 홀로 커 보이던 것(2026-09-16 대교). */}
        <label className="flex flex-col gap-1 text-[13px]">시작일<input type="date" aria-label="시작일" className={`${selectClass} h-9 w-[170px] py-0`} value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} /></label>
        <label className="flex flex-col gap-1 text-[13px]">종료일<input type="date" aria-label="종료일" className={`${selectClass} h-9 w-[170px] py-0`} value={to} onChange={e => { setTo(e.target.value); setPage(1); }} /></label>
        <SearchInput placeholder="계정·요청·기기 검색" value={search} onChange={setSearch} onSearch={() => { setQuery(search.trim()); setPage(1); }} />
      </div>
      {(error || dateError) && <p role="alert" className="mb-4 rounded border border-red/50 bg-red/15 p-3 text-red">{error || dateError}</p>}
      {loading && <ContentSkeleton label="이력을 불러오는 중…" variant="table" />}
      <div className={loading ? 'hidden' : 'contents'}>
      <p className="mb-3 text-[14px] text-text-dim">조회 {rows.length}건 · 검색 결과 {filtered.length}건</p>
      {/* 한 행 = "누가 · 무엇을 · 어디에" 한 문장 + 그 아래 세부 기록(P3·S17).
          디버깅용 원본 복사 열은 뺐다(SCDS-68). */}
      <div className="overflow-x-auto rounded border border-line"><table data-mobile-cards className="w-full text-left text-[14px]">
        <thead className="bg-panel-2 text-text-dim"><tr>{['일시 (한국 시간)', '내용', '대상 기기'].map(label => <th key={label} className="px-3 py-2.5 font-medium">{label}</th>)}</tr></thead>
        <tbody>{visible.map(row => <tr key={row.id} className="border-t border-line align-top">
          <td data-card="full" className="whitespace-nowrap px-3 py-3 max-md:text-[12px] max-md:text-text-dim">{dateTime(row.created_at)}</td>
          <td data-card="full" className="min-w-[280px] px-3 py-3">
            <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
              <span className="break-all font-medium" {...(row.actorId ? { title: `수행자 ID: ${row.actorId}` } : {})}>{row.actorLabel}</span>
              <span aria-hidden className="text-text-dim">·</span>
              <span>{row.actionLabel}</span>
              <span aria-hidden className="text-text-dim">·</span>
              <span className="text-text-dim">{row.groupName}</span>
            </div>
            <div className="mt-1 break-words text-[13px] text-text-dim">{row.details}</div>
          </td>
          {/* 한 대면 이름을 바로, 여러 대면 접어 둔다. 기기와 무관한 이력은 '해당 없음'(SCDS-88). */}
          <td className="min-w-[180px] px-3 py-3">{row.targets.length === 1 && !row.action.startsWith('approval.')
            ? <span>{row.targets[0]!.name ?? '삭제된 기기'}<span className="block break-all text-[12px] text-text-dim">{row.targets[0]!.id}</span></span>
            : row.targets.length ? <details><summary className="cursor-pointer">{row.action === 'approval.submit' ? '요청 당시' : row.action === 'approval.approve' ? '승인 당시' : '처리 당시'} {row.targets.length}대</summary>
            <ul className="mt-2 space-y-1">{row.targets.map(t => <li key={t.id}>{t.name ?? '삭제된 기기'}<span className="block break-all text-[12px] text-text-dim">{t.id}</span></li>)}</ul>
          </details> : <span className="text-text-dim">{row.noTargets}</span>}</td>
        </tr>)}{!visible.length && <tr><td colSpan={3} className="py-10 text-center text-text-dim">{error ? '이력을 표시할 수 없습니다.' : loading ? '불러오는 중…' : '표시할 이력이 없습니다.'}</td></tr>}</tbody>
      </table></div>
      {/* 어시스턴트 FAB(높이 48 + 여백 16)가 페이지네이션을 덮지 않도록 아래를 비운다(S4). */}
      <div className="mt-4 flex items-center justify-end gap-3 pb-24">
        <select aria-label="페이지당 이력 수" className={selectClass} value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>{[10, 20, 50].map(size => <option key={size} value={size}>{size}개씩</option>)}</select>
        <Button aria-label="이전 페이지" disabled={currentPage <= 1 || loading} onClick={() => setPage(currentPage - 1)}>‹</Button>
        <span>{currentPage} / {totalPages}</span><Button aria-label="다음 페이지" disabled={currentPage >= totalPages || loading} onClick={() => setPage(currentPage + 1)}>›</Button>
      </div>
      </div>
    </>}
  </PageContainer>;
}
